/* eslint-disable no-restricted-imports */
import React, {useEffect, useMemo, useRef} from 'react'
import {UIGameActionButton} from '../../../../common/UIGameActionButton/index.ts'
import {
    UIGameResourceCardsTooltipContent,
    type UIGameResourceCardsTooltipContentHandle,
} from '../../../../common/UIGameResourceCardsTooltipContent/index.ts'
import {useActionButtonCommonErrors} from '../../utils.ts'
import {T, Tooltip} from '@colonist/ui'
import {getCardsForPiece} from '@colonist/game-card-types'
import {type ActionStatus, PieceTypes} from '@colonist/game-base-types'
import IconKnightUpgrade from '@static/images/icon_knight_upgrade.svg'
import {useGameStore} from '../../../../store/GameStoreContext.ts'
import {usePlayerValidators} from '../../../../store/selectors/usePlayerValidators.ts'
import {ActionState} from '@colonist/game-states'
import {MapValidator, MechanicValidatorKnightPlayer} from '@colonist/game-mechanic-validators'
import {type TranslationObject} from '@colonist/i18n'
import {useUIGameSocketContext} from '../../../../contexts/UIGameSocketContext.tsx'
import {useCurrentStateValidator} from '../../../../store/selectors/useCurrentStateValidator.ts'

const cardsToUpgradeKnight = getCardsForPiece(PieceTypes.Knight)

export const UIGameActionButtonKnightUpgrade = ({
    className,
    onDisabledChange,
}: {
    onDisabledChange: (isDisabled: boolean) => void
    className?: string
}) => {
    const myColor = useGameStore((state) => state.myColor)
    const mechanicKnightState = useGameStore((state) => state.gameState.mechanicKnightState)
    const mapState = useGameStore((state) => state.gameState.mapState)
    const currentStateValidator = useCurrentStateValidator()
    const {getPlayerValidator, playerValidators} = usePlayerValidators()
    const playerValidator = getPlayerValidator(myColor)
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const tooltipRef = useRef<UIGameResourceCardsTooltipContentHandle>(null)
    const {sendUpgradeKnight, sendCancelAction, sendClearCornerHighlight} = useUIGameSocketContext()
    const {actionStatus, knightsThatCanBeUpgradedCount}: {
        actionStatus: ActionStatus
        knightsThatCanBeUpgradedCount: number
    } = useMemo(() => {
        const knightPlayerState = mechanicKnightState?.[myColor]

        if(!knightPlayerState) {
            return {actionStatus: {canTakeAction: false}, knightsThatCanBeUpgradedCount: 0}
        }
        const knightPlayerValidator = new MechanicValidatorKnightPlayer(playerValidator,
            new MapValidator(mapState), knightPlayerState)
        const knightsThatCanBeUpgradedCountLocal = knightPlayerValidator.getAmountOfKnightsEligibleForUpgrade()
        const canUpgradeKnightActionStatus = knightPlayerValidator.canPlayerUpgradeKnight()
        return {
            actionStatus: canUpgradeKnightActionStatus,
            knightsThatCanBeUpgradedCount: knightsThatCanBeUpgradedCountLocal,
        }
    }, [
        playerValidators,
        mapState,
        mechanicKnightState,
        myColor,
    ])

    const triggerFlickerCardsAnimation = () => {
        const notEnoughResources = !playerValidator.hasResourceCardsInHand(cardsToUpgradeKnight)
        if(notEnoughResources) {
            tooltipRef.current?.triggerFlickerAnimation()
        }
    }

    const disabled = hasCommonErrors || !actionStatus.canTakeAction
    const actionState = currentStateValidator.getPlayerActionState(myColor)
    const isUpgradingKnight = actionState === ActionState.UpgradeKnight && !disabled
    const helperText: TranslationObject | undefined = errorHelperText || actionStatus.errorMessageIfCantTakeAction

    useEffect(() => {
        onDisabledChange(disabled)
    }, [disabled])

    const onClick = () => {
        if(disabled) return
        if(isUpgradingKnight) {
            sendClearCornerHighlight()
            sendCancelAction()
        } else {
            sendUpgradeKnight()
        }
    }

    return <Tooltip
        position='bottom'
        title={<T id='strings:game.tooltips.upgradeKnight'/>}>
        <UIGameActionButton
            className={className}
            count={knightsThatCanBeUpgradedCount}
            countColor={myColor}
            disabled={disabled}
            helperText={helperText}
            highlight={isUpgradingKnight}
            iconPath={IconKnightUpgrade}
            onClickActionIfDisabled={triggerFlickerCardsAnimation}
            tooltipCustomMenuComponent={
                <UIGameResourceCardsTooltipContent
                    cardInventory={playerValidator.resourceCards.cards}
                    cardsToBuild={cardsToUpgradeKnight}
                    ref={tooltipRef}/>
            }
            onClick={onClick}
        />
    </Tooltip>
}
